import {
	faCheck,
	faChevronDown,
	faChevronUp,
	faFilter,
	faGrid2,
	faList,
	faMagnifyingGlass,
	faTag,
	faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	applyHeadlineToUnitPrice,
	applyStorefrontCatalogDefaults,
	type StorefrontCatalogDefaultsPlanModel,
} from '@supply-mover-2-0/core';
import type {
	ItemPromoHeadline,
	PortalCatalogFacetedQueryDto,
	PortalCategoryDto,
} from '@supply-mover-2-0/shared';
import {
	findStorefrontCatalogSortOption,
	resolveStorefrontCatalogSortOptions,
	storefrontCatalogSortValueFromParams,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import { appScrollToTop } from '../../lib/appScroll';
import {
	buildClearAllFacetParams,
	facetParamName,
	hasActiveFacetFilters,
	parseFacetFilters,
	parsePromoParam,
	PROMO_PARAM,
} from '../../lib/catalogFacetFilters';
import { formatCurrency } from '../../lib/formatCurrency';
import * as catalogService from '../../services/portalCatalogService';
import { getStorefrontPromos } from '../../services/portalDashboardService';
import { AddToCartControl } from '../AddToCartControl';
import { FavoriteButton } from '../FavoriteButton';
import { ItemDesignationBadges } from '../ItemDesignationBadges';
import { MobilePageHeader } from '../layout/MobilePageHeader';
import { Paginator } from '../Paginator';
import { ProductIdentifierLines } from '../ProductIdentifierLines';
import { ProductMetaLines } from '../ProductMetaLines';
import { CategoryTreeItem } from './CategoryTreeItem';
import { FilterRail } from './FilterRail';

const PAGE_SIZE = 24;

/**
 * The full catalog experience — category tree sidebar, faceted filter
 * rail, sort, grid/list toggle, pagination, product grid — extracted
 * from the /catalog page (PD-14547) so the SAME component powers two
 * surfaces:
 *
 *   - `CatalogPage` (/catalog route) renders it as the standalone page.
 *   - `CatalogWidget` renders it `embedded` on the storefront homepage,
 *     so admins can compose a shop page with promo/content widgets
 *     above or below the catalog.
 *
 * All filter/sort/page state lives in URL query params either way —
 * deep links and back/forward work identically on both surfaces. That
 * also means at most ONE instance should render per page (two would
 * fight over the same params); the storefront widget registry entry
 * documents the one-per-design expectation.
 *
 * `embedded` strips page-level chrome that makes no sense inside the
 * dashboard: the outer max-width page container and the mobile
 * "← Catalog" back-strip (the widget already lives on the page it
 * would navigate back to). Pagination scrolls to the top of the
 * browser itself rather than the window top, so widgets above the
 * catalog don't yank the viewport away from it.
 */
export function CatalogBrowser({
	embedded = false,
}: {
	embedded?: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, activeClientId } = usePortalAuth();
	const display = useDisplaySettings();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const containerRef = useRef<HTMLDivElement | null>(null);

	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	// Category-tree disclosure state. `categoriesExpanded` collapses the whole
	// Categories section; `expandedCategories` tracks per-category subcategory
	// disclosure (`| undefined` = no manual toggle yet, fall back to
	// "expanded when selected").
	const [categoriesExpanded, setCategoriesExpanded] = useState(true);
	const [expandedCategories, setExpandedCategories] = useState<
		Record<string, boolean | undefined>
	>({});
	const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
		const saved = localStorage.getItem('portal:catalog:viewMode');
		return saved === 'list' ? 'list' : 'grid';
	});

	const handleViewModeChange = useCallback((mode: 'grid' | 'list') => {
		setViewMode(mode);
		localStorage.setItem('portal:catalog:viewMode', mode);
	}, []);

	// Read filters from URL query params
	const searchQuery = searchParams.get('q') ?? '';
	const selectedCategory = searchParams.get('category') ?? '';
	const selectedSubcategory = searchParams.get('subcategory') ?? '';
	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const activeSort = searchParams.get('sortBy') ?? '';
	const activeSortOrder = searchParams.get('sortOrder') ?? '';
	// Per-customer toolbar toggles. The general catalog toggles (in stock /
	// on promo / on order / short date) are read by parseFacetFilters via the
	// site's configured filter fields, not here.
	const filterFavorite = searchParams.get('isFavorite') === 'true';
	const filterPrevPurchased =
		searchParams.get('isPreviouslyPurchased') === 'true';

	// Fetch catalog configuration. Allowed for anonymous visitors when the
	// storefront permits it (display.isAnonymousVisitor === true means the
	// bootstrap already confirmed anonymous browsing is enabled). The API
	// uses `@Security.PublicPortalOptional()` so it returns the same
	// payload either way.
	const { data: catalogConfig, isFetched: catalogConfigFetched } = useQuery({
		queryKey: ['catalog', 'config'],
		queryFn: () => catalogService.getCatalogConfig(token ?? ''),
		enabled: token != null || display.isAnonymousVisitor,
		staleTime: 300_000,
	});

	const availableSortOptions = catalogConfig?.data?.sortOptions ?? [];
	// The site's exposed faceted filters (per-site subset/order/labels,
	// visitor-gated server-side) — drives the FilterRail AND which URL facet
	// params get parsed into the query.
	const filterFields = useMemo(
		() => catalogConfig?.data.filterFields ?? [],
		[catalogConfig],
	);

	// PD-15272: the brand's default filters and sort, expressed as the URL
	// params the catalog already reads. The server has dropped any default
	// this visitor may not use (In Stock when stock is hidden, a price sort
	// when pricing is hidden), so only the param names are worked out here.
	const catalogDefaultsPlan =
		useMemo((): StorefrontCatalogDefaultsPlanModel => {
			const defaultFilterKeys = new Set(
				catalogConfig?.data.defaultFilters ?? [],
			);
			const defaultSort = findStorefrontCatalogSortOption(
				catalogConfig?.data.defaultSort,
			);

			return {
				filterParams: filterFields
					.filter((field) => defaultFilterKeys.has(field.key))
					.map((field) => facetParamName(field)),
				sort:
					defaultSort != null
						? { sortBy: defaultSort.sortBy, sortOrder: defaultSort.sortOrder }
						: null,
			};
		}, [catalogConfig, filterFields]);

	// The URL to switch to so the defaults apply, or null when there is
	// nothing to do (no defaults, already applied on this visit, or the link
	// carried its own filter or sort). Applied by replacing the history entry
	// so Back does not return to the pre-default URL and re-apply them.
	const pendingDefaultParams = useMemo(
		() =>
			catalogConfigFetched
				? applyStorefrontCatalogDefaults(searchParams, catalogDefaultsPlan)
				: null,
		[catalogConfigFetched, searchParams, catalogDefaultsPlan],
	);

	useEffect(() => {
		if (pendingDefaultParams != null) {
			setSearchParams(pendingDefaultParams, { replace: true });
		}
	}, [pendingDefaultParams, setSearchParams]);

	// Build the faceted search request from the URL. Sort translation:
	// 'recently_added' historically wrote sortBy=createdDate, which isn't an
	// indexed field — map it to externalCreatedDate (the indexed source-system
	// date) so old deep links keep sorting sensibly.
	const facetedQuery = useMemo((): PortalCatalogFacetedQueryDto => {
		const filters = parseFacetFilters(searchParams, filterFields, {
			selectedCategory,
			selectedSubcategory,
			isAuthenticated,
			hasActiveClient: activeClientId != null,
		});
		const effectiveSortBy =
			activeSort === 'createdDate' ? 'externalCreatedDate' : activeSort;

		return {
			textQuery: searchQuery.length > 0 ? searchQuery : undefined,
			filters: Object.keys(filters).length > 0 ? filters : undefined,
			page: currentPage,
			limit: PAGE_SIZE,
			sortBy: effectiveSortBy.length > 0 ? effectiveSortBy : undefined,
			sortOrder:
				activeSortOrder.length > 0
					? (activeSortOrder as 'ASC' | 'DESC')
					: undefined,
		};
	}, [
		searchParams,
		filterFields,
		selectedCategory,
		selectedSubcategory,
		isAuthenticated,
		activeClientId,
		searchQuery,
		currentPage,
		activeSort,
		activeSortOrder,
	]);

	// Fetch catalog items via the faceted endpoint (filters + pagination +
	// live facet counts in one OpenSearch pass). Gated on the config query
	// having SETTLED (fetched, success or error) so facet params in the URL
	// aren't silently ignored on the first fetch — filterFields drives which
	// params parse into the request. On config error the page still loads,
	// just without the rail. Also held while the brand's defaults are being
	// written into the URL, so the catalog does not load unfiltered first and
	// then again with the defaults.
	const { data: catalogData, isLoading: catalogLoading } = useQuery({
		queryKey: ['catalog', 'faceted', facetedQuery, display.isAnonymousVisitor],
		queryFn: () =>
			catalogService.facetedSearchCatalog(token ?? '', facetedQuery),
		enabled:
			(token != null || display.isAnonymousVisitor) &&
			catalogConfigFetched &&
			pendingDefaultParams == null,
		staleTime: 30_000,
	});

	// Fetch categories for sidebar / mobile filter
	const { data: categoriesData } = useQuery({
		queryKey: ['catalog', 'categories', display.isAnonymousVisitor],
		queryFn: () => catalogService.getCategories(token ?? ''),
		enabled: token != null || display.isAnonymousVisitor,
		staleTime: 60_000,
	});

	const items = catalogData?.data ?? [];
	const pagination = catalogData?.pagination ?? null;
	const facets = catalogData?.facets ?? [];
	const categories = categoriesData?.data ?? [];

	// --- Handlers ---

	const updateSearchParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handleCategorySelect = useCallback(
		(category: string, subcategory?: string) => {
			updateSearchParams({
				category: category.length > 0 ? category : null,
				subcategory:
					subcategory != null && subcategory.length > 0 ? subcategory : null,
				page: null,
			});

			if (category.length > 0) {
				// Selecting a category always reveals its subcategories, even if
				// the user had manually collapsed that category earlier.
				setExpandedCategories((prev) => ({ ...prev, [category]: true }));
			}

			setMobileFiltersOpen(false);
		},
		[updateSearchParams],
	);

	const handleClearFilters = useCallback(() => {
		updateSearchParams({
			q: null,
			category: null,
			subcategory: null,
			page: null,
			sortBy: null,
			sortOrder: null,
			// Every facet-rail param (f.*) plus the legacy boolean toggles.
			...buildClearAllFacetParams(searchParams),
		});
		setMobileFiltersOpen(false);
	}, [updateSearchParams, searchParams]);

	/**
	 * Apply a facet-rail URL mutation. Wraps setSearchParams so every rail
	 * interaction resets pagination — a changed filter set makes the current
	 * page number meaningless.
	 */
	const handleFacetUpdate = useCallback(
		(mutate: (next: URLSearchParams) => void) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				mutate(next);
				next.delete('page');
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateSearchParams({ page: page > 1 ? String(page) : null });
			// Embedded on the dashboard, "top of page" would scroll PAST the
			// catalog to whatever widgets sit above it — return to the top of
			// the browser itself instead.
			if (embedded && containerRef.current != null) {
				containerRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			} else {
				appScrollToTop('smooth');
			}
		},
		[updateSearchParams, embedded],
	);

	/** Toggle a boolean filter param on/off in the URL */
	const handleFilterToggle = useCallback(
		(paramName: string) => {
			const isActive = searchParams.get(paramName) === 'true';
			updateSearchParams({
				[paramName]: isActive ? null : 'true',
				page: null,
			});
		},
		[searchParams, updateSearchParams],
	);

	/**
	 * Handle sort option selection. Each option's URL params come from the
	 * shared sort table (the same one the Portal Site editor's Default sort
	 * picker uses), so a brand's default sort and a shopper's pick write the
	 * identical params. The empty "Sort by" choice clears the sort back to the
	 * standard order.
	 */
	const handleSortChange = useCallback(
		(value: string) => {
			const option = findStorefrontCatalogSortOption(value);

			updateSearchParams({
				sortBy: option?.sortBy ?? null,
				sortOrder: option?.sortOrder ?? null,
				page: null,
			});
		},
		[updateSearchParams],
	);

	/** Derive the current sort dropdown value from URL params */
	const currentSortValue = useMemo(
		() =>
			storefrontCatalogSortValueFromParams(activeSort, activeSortOrder) ?? '',
		[activeSort, activeSortOrder],
	);

	const hasActiveFilters =
		searchQuery.length > 0 ||
		selectedCategory.length > 0 ||
		hasActiveFacetFilters(searchParams);

	// Promo scope, set when the customer arrives from a promo tile or the
	// promo detail page. Its name is resolved from the eligible-promo list
	// the storefront already loads, so this usually costs nothing — the
	// homepage populates the same react-query key.
	const activePromoId = parsePromoParam(searchParams);
	const eligiblePromosQuery = useQuery({
		queryKey: ['portal', 'dashboard', 'promos'],
		queryFn: () => getStorefrontPromos(token),
		staleTime: 30_000,
		enabled: activePromoId != null,
	});
	const activePromoName =
		activePromoId == null
			? null
			: (eligiblePromosQuery.data?.data?.find((p) => p.id === activePromoId)
					?.name ?? null);

	/**
	 * Toolbar toggle chips — per-CUSTOMER lenses only (favorites, previously
	 * purchased). The general catalog toggles (in stock / on promo / on order /
	 * short date) are site-configured facets now and render in the FilterRail;
	 * these two are deliberately NOT site-configurable (they're about the
	 * customer, not the catalog) so they live up here, shown contextually to
	 * authenticated visitors. Previously Purchased additionally requires the
	 * site to allow purchasing — on a browse-only storefront a "what you've
	 * ordered" lens is noise.
	 */
	const filterToggles = useMemo(() => {
		const toggles: Array<{
			key: string;
			param: string;
			label: string;
			active: boolean;
		}> = [];

		if (isAuthenticated && activeClientId != null && display.canPurchase) {
			toggles.push({
				key: 'previously_purchased',
				param: 'isPreviouslyPurchased',
				label: 'Previously Purchased',
				active: filterPrevPurchased,
			});
		}

		if (isAuthenticated) {
			toggles.push({
				key: 'favorites',
				param: 'isFavorite',
				label: 'Favorites',
				active: filterFavorite,
			});
		}

		return toggles;
	}, [
		isAuthenticated,
		activeClientId,
		display.canPurchase,
		filterPrevPurchased,
		filterFavorite,
	]);

	/**
	 * Sort dropdown options from the tenant's enabled sort keys — Relevance is
	 * always included first, and the keys' own order is kept so discovery
	 * sorts (popular, dates) surface ahead of name and price.
	 */
	const sortOptions = useMemo(
		() =>
			resolveStorefrontCatalogSortOptions(availableSortOptions).map(
				(option) => ({ value: option.value, label: option.label }),
			),
		[availableSortOptions],
	);

	// --- Category sidebar content (shared between desktop sidebar and mobile sheet) ---
	const renderCategoryList = (): JSX.Element => (
		<div className="space-y-1">
			{/* All items option. Same -mx-2/px-2 bleed as CategoryTreeItem so
			    every label in the tree lines up with the "Categories" heading. */}
			<button
				type="button"
				onClick={() => {
					handleCategorySelect('');
				}}
				className={`-mx-2 w-[calc(100%+1rem)] text-left px-2 py-2 rounded-lg text-sm transition-colors ${
					selectedCategory.length === 0
						? 'bg-portal-accent/10 text-portal-accent font-medium'
						: 'text-strong hover:bg-sunken'
				}`}
			>
				{t('All Products')}
			</button>

			{categories.map((cat: PortalCategoryDto) => {
				// Disclosure: a manual chevron toggle wins; with no manual state
				// the selected category's subcategories show (pre-disclosure
				// behavior preserved).
				const subcategoriesShown =
					expandedCategories[cat.category] ?? selectedCategory === cat.category;

				return (
					<CategoryTreeItem
						key={cat.category}
						category={cat}
						isSelected={
							selectedCategory === cat.category &&
							selectedSubcategory.length === 0
						}
						isSelectedBranch={selectedCategory === cat.category}
						selectedSubcategory={selectedSubcategory}
						subcategoriesShown={subcategoriesShown}
						onSelect={handleCategorySelect}
						onToggleExpanded={() => {
							setExpandedCategories((prev) => ({
								...prev,
								[cat.category]: !subcategoriesShown,
							}));
						}}
					/>
				);
			})}
		</div>
	);

	// --- Collapsible "Categories" section (header toggle + tree), shared by
	// the desktop sidebar and the mobile sheet ---
	const renderCategorySection = (): JSX.Element => (
		<div>
			<button
				type="button"
				onClick={() => {
					setCategoriesExpanded((prev) => !prev);
				}}
				className="w-full flex items-center justify-between gap-2 mb-3 text-left"
				aria-expanded={categoriesExpanded}
			>
				<h2 className="text-sm font-semibold text-default">
					{t('Categories')}
				</h2>
				<FontAwesomeIcon
					icon={categoriesExpanded ? faChevronDown : faChevronUp}
					className="text-xs text-brand-muted"
				/>
			</button>
			{categoriesExpanded && renderCategoryList()}
		</div>
	);

	// One bar per product identifier line the card shows (PD-16126), at every
	// width, like the live card's lines: the item number always, then NDC and
	// Size when the storefront shows them.
	const identifierSkeletonLineCount =
		1 +
		[display.showItemNdc, display.showItemSize].filter((isShown) => isShown)
			.length;

	// --- Skeleton cards for loading state ---
	// Grid + per-card structure must MIRROR the real grid below, otherwise
	// the page reflows when items land — placeholder cards in 2 columns
	// suddenly snapping to 3 columns at first paint registered as a bug
	// in the hf staging review. Keep this in sync with the live grid:
	// columns, gaps, padding, manufacturer/designations visibility, etc.
	const renderSkeletons = (): JSX.Element => (
		<div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
			{Array.from({ length: PAGE_SIZE }, (_, i) => (
				<div
					key={i}
					className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden animate-pulse"
				>
					<div className="aspect-square bg-brand-input" />
					<div className="p-2 sm:p-3">
						{/* Title placeholder — 2 lines on mobile, 2 lines at sm+. */}
						<div className="h-3 sm:h-4 bg-brand-input rounded mb-2 w-3/4" />
						<div className="h-3 sm:h-4 bg-brand-input rounded mb-2 w-1/2" />
						{/* Manufacturer line — hidden on mobile (matches real card). */}
						<div className="hidden sm:block h-3 bg-brand-input rounded mb-2 w-1/2" />
						{/* Item number / NDC / Size lines — shown at every width. */}
						{Array.from({ length: identifierSkeletonLineCount }, (_, j) => (
							<div
								key={j}
								className="h-2.5 bg-brand-input rounded mb-1 w-2/3"
							/>
						))}
						{/* Price/stock row */}
						<div className="h-4 bg-brand-input rounded w-1/3" />
					</div>
				</div>
			))}
		</div>
	);

	// --- Format price for display ---
	const renderPrice = (
		price: string | null,
		contractPrice: string | null,
		promoHeadline?: ItemPromoHeadline | null,
	): JSX.Element => {
		// PD-14239 / PD-14775: hide ALL pricing UI when catalog pricing is
		// off for this visitor — per-user hidePricing or the site-wide
		// hideCatalogPricing setting (canSeeCatalogPricing=false). The server
		// already nulls price/contractPrice for these visitors; returning
		// nothing here also removes the "Call for price" placeholder so no
		// price slot remains. This also covers the anonymous pricing-hidden
		// storefront (canSeeCatalogPricing=false for anon too).
		if (!display.canSeeCatalogPricing) {
			return <></>;
		}

		const formattedContract = formatCurrency(contractPrice);
		const formattedPrice = formatCurrency(price);
		const displayFormatted = formattedContract ?? formattedPrice;

		if (displayFormatted == null) {
			// For a signed-in user (or anon on a pricing-allowed storefront)
			// a null price genuinely means the item is unpriced — show
			// "Call for price" so we don't tell them to fix something signing
			// in won't fix.
			return <span className="text-sm text-faint">{t('Call for price')}</span>;
		}

		// New pricing model (see ProductDetailPage for the canonical
		// comment): contract price IS the price (not a discount), no
		// strikethrough on contract, no "Contract Price" label. Promos
		// strike against whatever base the lookup produced. A muted
		// PromoChip surfaces the campaign so the customer knows why
		// the price moved without competing with stock chips on a
		// narrow card.
		const listNum = price != null ? parseFloat(price) : NaN;
		const contractNum = contractPrice != null ? parseFloat(contractPrice) : NaN;
		const basePrice = Number.isFinite(contractNum)
			? contractNum
			: Number.isFinite(listNum)
				? listNum
				: NaN;
		const promoFinal =
			Number.isFinite(basePrice) && promoHeadline != null
				? applyHeadlineToUnitPrice(basePrice, promoHeadline)
				: null;
		const promoActive = promoFinal != null && promoFinal < basePrice;

		if (promoActive && promoHeadline != null) {
			// Instacart-style sale highlight. Final price gets a soft
			// amber background tile so it reads at a glance against
			// the rest of the card; original price strikes through
			// next to it. Stacks horizontally to fit a 110px-wide
			// mobile card without wrapping.
			//
			// We DELIBERATELY don't render a "% off" green eyebrow OR
			// the campaign-name PromoChip pill here. The discount
			// badge already sits over the product image (top-left
			// corner) and is the canonical "this is on sale"
			// affordance — repeating "25% off" as text below the
			// price AND as a campaign chip at the bottom of the card
			// produced three copies of the same information on a
			// 110px-wide card and pushed everything else (stock pill,
			// add button) off the visible footprint. The price tile
			// + strikethrough is enough numeric context; the badge
			// over the image is enough campaign context. Customers
			// who want the campaign name tap into the product detail
			// page where the full PromoChip lives.
			return (
				<div className="flex items-center gap-1.5 flex-wrap">
					<span className="inline-flex items-baseline px-1.5 py-0.5 rounded-md text-default text-xs sm:text-sm font-bold bg-warning-badge text-warning-tint-fg border border-warning-tint">
						{t(formatCurrency(promoFinal) ?? '')}
					</span>
					<span className="text-[11px] sm:text-xs text-faint line-through">
						{t(formatCurrency(basePrice) ?? '')}
					</span>
				</div>
			);
		}

		return (
			<span className="text-xs sm:text-sm font-bold text-default">
				{t(formatCurrency(basePrice) ?? '')}
			</span>
		);
	};

	return (
		<div
			ref={containerRef}
			className={embedded ? 'w-full' : 'px-4 py-4 max-w-6xl mx-auto'}
		>
			{/* Mobile-only "← Catalog" strip (page surface only — embedded on
			    the dashboard there is nowhere to go "back" to). Without this
			    the only way back to the dashboard from a deep catalog scroll
			    is the BottomNav's Home tab, which not every customer notices.
			    Same pattern Invoices / Orders / Account already use. Hidden
			    at md+. */}
			{!embedded && <MobilePageHeader title="Catalog" backPath="/dashboard" />}

			{/* ── Toolbar — filters, sort, view, and count in one unified row ── */}
			<div className="flex items-center gap-3 mb-4 flex-wrap">
				{/* Filter toggles — active state combines color + a check icon
				    so the difference reads at a glance instead of relying
				    purely on a fill swap. */}
				{filterToggles.length > 0 && (
					<div className="flex items-center gap-1.5 flex-wrap">
						{filterToggles.map((toggle) => (
							<button
								key={toggle.key}
								type="button"
								onClick={() => {
									handleFilterToggle(toggle.param);
								}}
								aria-pressed={toggle.active}
								className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
									toggle.active
										? 'bg-portal-accent text-white border-portal-accent shadow-sm'
										: 'bg-brand-surface text-muted border-brand-border hover:border-portal-accent/60'
								}`}
							>
								{toggle.active && (
									<FontAwesomeIcon icon={faCheck} className="text-[10px]" />
								)}
								{t(toggle.label)}
							</button>
						))}
					</div>
				)}

				{/* Spacer — pushes sort/view to the right */}
				<div className="flex-1" />

				{/* Results count */}
				{pagination != null && !catalogLoading && (
					<span className="text-xs text-brand-muted hidden sm:inline whitespace-nowrap">
						{t(String(pagination.total))}{' '}
						{pagination.total === 1 ? t('result') : t('results')}
					</span>
				)}

				{/* Sort dropdown */}
				{sortOptions.length > 0 && (
					<div className="relative">
						<select
							value={currentSortValue}
							onChange={(e) => {
								handleSortChange(e.target.value);
							}}
							className="appearance-none pl-3 pr-7 py-1.5 text-xs rounded-lg bg-brand-surface border border-brand-border text-strong cursor-pointer"
							aria-label={t('Sort by')}
						>
							<option value="">{t('Sort by')}</option>
							{sortOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{t(opt.label)}
								</option>
							))}
						</select>
						<FontAwesomeIcon
							icon={faChevronDown}
							className="absolute right-2 top-1/2 -translate-y-1/2 text-faint text-[10px] pointer-events-none"
						/>
					</div>
				)}

				{/* View toggle — grid / list */}
				<div className="flex items-center border border-brand-border rounded-lg overflow-hidden">
					<button
						type="button"
						onClick={() => {
							handleViewModeChange('grid');
						}}
						className={`p-1.5 flex items-center justify-center transition-colors ${
							viewMode === 'grid'
								? 'bg-portal-accent text-white'
								: 'bg-brand-surface text-brand-muted hover:text-strong'
						}`}
						aria-label={t('Grid view')}
					>
						<FontAwesomeIcon icon={faGrid2} className="text-xs" />
					</button>
					<button
						type="button"
						onClick={() => {
							handleViewModeChange('list');
						}}
						className={`p-1.5 flex items-center justify-center transition-colors ${
							viewMode === 'list'
								? 'bg-portal-accent text-white'
								: 'bg-brand-surface text-brand-muted hover:text-strong'
						}`}
						aria-label={t('List view')}
					>
						<FontAwesomeIcon icon={faList} className="text-xs" />
					</button>
				</div>

				{/* Mobile filters button — opens the categories + facet-rail sheet */}
				<button
					type="button"
					onClick={() => {
						setMobileFiltersOpen(true);
					}}
					className="md:hidden flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-muted bg-brand-surface border border-brand-border rounded-lg"
				>
					<FontAwesomeIcon icon={faFilter} />
					<span>{t('Filters')}</span>
				</button>
			</div>

			{/* ── Active filter/search chips — removable tags for what's currently applied ── */}
			{hasActiveFilters && (
				<div className="flex flex-wrap items-center gap-2 mb-4">
					{searchQuery.length > 0 && (
						<span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-portal-accent/10 text-portal-accent text-xs rounded-full">
							{t('Search')}: {t(searchQuery)}
							<button
								type="button"
								onClick={() => {
									updateSearchParams({ q: null, page: null });
								}}
								className="hover:text-portal-accent"
								aria-label={t('Clear search')}
							>
								<FontAwesomeIcon icon={faTimes} className="text-[10px]" />
							</button>
						</span>
					)}
					{selectedCategory.length > 0 && (
						<span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-portal-accent/10 text-portal-accent text-xs rounded-full">
							{t(selectedCategory)}
							{selectedSubcategory.length > 0 && (
								<>
									{' / '}
									{t(selectedSubcategory)}
								</>
							)}
							<button
								type="button"
								onClick={() => {
									updateSearchParams({
										category: null,
										subcategory: null,
										page: null,
									});
								}}
								className="hover:text-portal-accent"
								aria-label={t('Clear category')}
							>
								<FontAwesomeIcon icon={faTimes} className="text-[10px]" />
							</button>
						</span>
					)}
					{/*
					 * Promo scope. Rendered as a removable chip like any other
					 * filter, because that is what it is from the customer's
					 * point of view — arriving from a promo tile silently
					 * narrows the catalog, and without this the reduced result
					 * count looks like missing inventory. Falls back to the id
					 * when the promo's name hasn't resolved (or is no longer
					 * eligible) so the scope is never invisible.
					 */}
					{activePromoId != null && (
						<span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-portal-sale/10 text-portal-sale text-xs rounded-full">
							<FontAwesomeIcon icon={faTag} className="text-[10px]" />
							{t('Promotion')}:{' '}
							{activePromoName != null
								? t(activePromoName)
								: `#${activePromoId}`}
							<button
								type="button"
								onClick={() => {
									updateSearchParams({ [PROMO_PARAM]: null, page: null });
								}}
								className="hover:text-portal-sale"
								aria-label={t('Clear promotion filter')}
							>
								<FontAwesomeIcon icon={faTimes} className="text-[10px]" />
							</button>
						</span>
					)}
					<button
						type="button"
						onClick={handleClearFilters}
						className="text-xs text-brand-muted hover:text-strong underline"
					>
						{t('Clear all')}
					</button>
				</div>
			)}

			{/* ── Main content: sidebar + product grid ── */}
			<div className="flex gap-6">
				{/* Desktop sidebar — category tree + the site-configured facet rail.
				    Sticky panel scrolls internally so a long facet list doesn't
				    push the page. */}
				<aside className="hidden md:block w-56 shrink-0">
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
						{renderCategorySection()}
						{filterFields.length > 0 && (
							<div className="mt-4 pt-4 border-t border-brand-border">
								<h2 className="text-sm font-semibold text-default mb-3">
									{t('Filters')}
								</h2>
								<FilterRail
									fields={filterFields}
									facets={facets}
									searchParams={searchParams}
									onUpdate={handleFacetUpdate}
								/>
							</div>
						)}
					</div>
				</aside>

				{/* Product grid area */}
				<div className="flex-1 min-w-0">
					{/* Loading state */}
					{catalogLoading && renderSkeletons()}

					{/* Product grid view */}
					{!catalogLoading && items.length > 0 && viewMode === 'grid' && (
						// Mobile: 3 columns of compact cards rather than 2.
						// 2-col mobile (~175px each) felt oversized — every
						// fold of the screen showed only 2-4 SKUs, customers
						// had to scroll a lot for what reads as "a small
						// catalog". Mainstream Asian-grocery references
						// (Weee, Yamibuy) ship 3-col mobile and that's the
						// density customers expect on a phone. The card
						// content (square image + 2-line title + price +
						// add-to-cart button) still fits at ~110px wide —
						// see the inner sizing tweaks for compact text and
						// tighter padding.
						<div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-5">
							{items.map((item) => (
								<div
									key={item.id}
									// Borderless / chromeless card. Instacart-style "let
									// the products do the talking" — drop the surface +
									// border + rounded-xl that turned every card into a
									// little box, so the items read as a clean grid on
									// the page background. Image still has a subtle bg
									// behind it to handle products with transparent
									// PNGs. Hover gets a soft shadow only on devices
									// that can hover (avoids a stuck shadow on touch
									// devices after a tap).
									className="relative flex flex-col rounded-xl transition-shadow [@media(hover:hover)]:hover:shadow-md"
								>
									{/* Favorite star — top-right of card. Auth-only: anon
									    visitors can't favorite without an account anyway,
									    and showing a star they can't use just teases them
									    into a sign-in wall. Logged-in users see + use it. */}
									{isAuthenticated && (
										<div className="absolute top-2 right-2 z-10">
											<FavoriteButton
												itemId={item.id}
												isFavorite={item.isFavorite === true}
												onToggled={() => {
													queryClient.invalidateQueries({
														queryKey: ['catalog', 'items'],
													});
												}}
											/>
										</div>
									)}

									{/* Clickable area — image + info navigate to detail.
									    Rendered as a role="button" div (NOT a <button>) so we
									    can absolutely-position the floating AddToCartControl,
									    the favorite star, and any other interactive
									    controls inside it. <button> can't legally contain
									    nested <button> children — React 18 surfaces that as
									    an "In HTML, <button> cannot be a descendant of
									    <button>" hydration error and we lose accessibility
									    on the inner controls (the outer button captures the
									    click). The div + role + tabIndex + Enter/Space
									    handler keeps keyboard activation working without
									    nesting button elements. */}
									<div
										role="button"
										tabIndex={0}
										onClick={() => navigate(`/product/${item.id}`)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												navigate(`/product/${item.id}`);
											}
										}}
										aria-label={item.description ?? undefined}
										className="text-left flex-1 flex flex-col cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent rounded-xl"
									>
										{/* Product image — fixed aspect-square. Wrapper div is
										    `relative` so absolutely-positioned overlays
										    (badge, floating + button) anchor to it.
										    Inner image div clips the actual <img> with
										    object-cover; the outer relative is NOT
										    overflow-hidden so the floating + button can
										    extend below the image edge into the title
										    area (Instacart-style boundary overlap). */}
										<div className="relative aspect-square">
											<div className="absolute inset-0 bg-brand-input rounded-md sm:rounded-none overflow-hidden">
												<ResilientImage
													src={item.primaryImageUrl}
													alt={item.description ?? ''}
													className="w-full h-full object-cover"
													loading="lazy"
													onLikelyStaleUrl={() => {
														void queryClient.invalidateQueries({
															queryKey: ['catalog', 'items'],
														});
													}}
													fallback={<ImagePlaceholder />}
												/>
											</div>
											{item.promoHeadline != null &&
												item.promoHeadline.displayBadgeInLists && (
													// Smaller and tighter on mobile (3-col cards
													// have ~110px width, every pixel of inset
													// matters). Sm+ keeps the original
													// readable-at-a-distance sizing.
													<span className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 inline-flex items-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide bg-warning-badge text-warning rounded-full px-1.5 sm:px-2 py-px sm:py-0.5 shadow">
														{t(item.promoHeadline.badgeText)}
													</span>
												)}
											{/* Floating add-to-cart control overlapping the
											    image bottom-right corner. `bottom-0 right-2
											    translate-y-1/2` puts the button's vertical
											    center exactly on the image boundary so it
											    half-floats over the image and half over the
											    card body — Instacart / Weee pattern.
											    Requires the parent NOT to be overflow-hidden
											    (we moved the image clip into a nested
											    inner div); the parent itself just provides
											    the relative coordinate space. */}
											{display.canPurchase && (
												<div className="absolute bottom-0 right-2 translate-y-1/2 z-[5]">
													<AddToCartControl
														itemId={item.id}
														variant="floating"
														inStock={item.inStock}
														deaSchedule={item.designations.deaSchedule}
													/>
												</div>
											)}
										</div>
										{/* Product info — flex column with reserved slots so cards stay aligned regardless of which fields are populated */}
										<div className="pt-5 sm:pt-6 flex-1 flex flex-col">
											{/* Title: always reserves 2 lines (line-height 1.25rem * 2) */}
											<p className="text-xs sm:text-sm font-medium text-default line-clamp-2 mb-1 sm:mb-1.5">
												{t(item.description ?? '')}
											</p>
											{/* Manufacturer: hidden on the 3-col mobile grid (no
											    horizontal room) and reserved to a single line at sm+
											    where the cards have the width to read it. nbsp keeps
											    the row height stable when the field is empty. */}
											<p className="hidden sm:block text-xs text-brand-muted mb-1 truncate">
												{item.manufacturer != null ? t(item.manufacturer) : ' '}
											</p>
											{/* Designations: hidden on the 3-col mobile grid (the
											    badges fight the title for the same horizontal
											    space). Reserved a row at sm+ so cards stay
											    aligned regardless of which fields are populated. */}
											<div className="hidden sm:block mb-1">
												<ItemDesignationBadges
													designations={item.designations}
												/>
											</div>
											{/* PD-16126: the item number, plus the opt-in NDC and
											    Size lines. Unlike the manufacturer and
											    designations rows these show on the 3-col mobile
											    grid too — they are short, and they are what a
											    pharmacy buyer matches against. */}
											<ProductIdentifierLines
												sku={item.externalId}
												ndc={item.ndc}
												size={item.size}
												display={display}
												variant="card"
												className="mb-1"
											/>
											{/* Price — pinned to the bottom of the card. The
											    stock pill that used to share this row was
											    removed: per-item availability is conveyed by
											    the floating add-to-cart control (disabled
											    ban-icon circle when out of stock). */}
											<div className="mt-auto">
												{renderPrice(
													item.price,
													item.contractPrice,
													item.promoHeadline,
												)}
												{/* PD-13692: unit (per UOM), on-hand, expiration */}
												<ProductMetaLines
													price={item.price}
													unitOfMeasure={item.unitOfMeasure}
													quantityOnHand={item.quantityOnHand}
													expirationDate={item.expirationDate}
													display={display}
													className="mt-1"
												/>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					)}

					{/* Product list view */}
					{!catalogLoading && items.length > 0 && viewMode === 'list' && (
						<div className="space-y-2">
							{items.map((item) => (
								<div
									key={item.id}
									className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden hover:shadow-sm transition-shadow flex items-stretch gap-3 p-3"
								>
									{/* Thumbnail */}
									<button
										type="button"
										onClick={() => navigate(`/product/${item.id}`)}
										className="w-20 h-20 shrink-0 bg-brand-input rounded-lg overflow-hidden self-center"
									>
										<ResilientImage
											src={item.primaryImageUrl}
											alt={item.description ?? ''}
											className="w-full h-full object-cover"
											loading="lazy"
											onLikelyStaleUrl={() => {
												void queryClient.invalidateQueries({
													queryKey: ['catalog', 'items'],
												});
											}}
											fallback={<ImagePlaceholder size="sm" />}
										/>
									</button>

									{/* Info column: 3 lines stacked — name (2-line clamp),
									    manufacturer + sku, price. Generous min-width-0 so
									    long names wrap rather than push the action column. */}
									<button
										type="button"
										onClick={() => navigate(`/product/${item.id}`)}
										className="flex-1 min-w-0 text-left flex flex-col justify-center gap-1"
									>
										<p className="text-sm font-medium text-default line-clamp-2 leading-snug">
											{t(item.description ?? '')}
										</p>
										<div className="flex items-center gap-1.5 text-xs text-brand-muted truncate">
											{item.manufacturer != null && (
												<span className="truncate">{t(item.manufacturer)}</span>
											)}
											{item.manufacturer != null && item.externalId != null && (
												<span className="text-disabled">{t('·')}</span>
											)}
											{item.externalId != null && (
												<span className="text-subtle truncate">
													{t('#')}
													{t(item.externalId)}
												</span>
											)}
											<ItemDesignationBadges designations={item.designations} />
										</div>
										{/* PD-16126: opt-in "NDC … · Size …" row; the item number
										    is already on the line above. */}
										<ProductIdentifierLines
											sku={item.externalId}
											ndc={item.ndc}
											size={item.size}
											display={display}
											variant="inline"
										/>
										<div className="flex items-center gap-2 flex-wrap">
											<span className="text-sm font-semibold text-default">
												{renderPrice(
													item.price,
													item.contractPrice,
													item.promoHeadline,
												)}
											</span>
											{/* On-sale badge rendered inline alongside the price
											    so list-view rows give the same "savings" cue the
											    grid view shows in the image overlay corner. The
											    strikethrough-only treatment was easy to miss in
											    a dense list. */}
											{item.promoHeadline != null &&
												item.promoHeadline.displayBadgeInLists && (
													<span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-warning-badge text-warning rounded-full px-2 py-0.5">
														{t(item.promoHeadline.badgeText)}
													</span>
												)}
										</div>
										{/* PD-13692: unit (per UOM), on-hand, expiration */}
										<ProductMetaLines
											price={item.price}
											unitOfMeasure={item.unitOfMeasure}
											quantityOnHand={item.quantityOnHand}
											expirationDate={item.expirationDate}
											display={display}
										/>
									</button>

									{/* Action column: stacked vertical — favorite on top
									    (auth-only), compact add control below (purchase-
									    enabled storefronts only). Hidden entirely for an
									    anonymous visitor on a browse-only / pricing-gated
									    storefront so the row collapses to image + info. */}
									{(isAuthenticated || display.canPurchase) && (
										<div className="shrink-0 flex flex-col items-end justify-between gap-2 w-28">
											{isAuthenticated && (
												<FavoriteButton
													itemId={item.id}
													isFavorite={item.isFavorite === true}
													onToggled={() => {
														queryClient.invalidateQueries({
															queryKey: ['catalog', 'items'],
														});
													}}
												/>
											)}
											{display.canPurchase && (
												// Use the same `floating` control the grid view
												// uses, so the customer's interaction pattern
												// stays consistent across view modes (round +
												// button out-of-cart, pill stepper in-cart).
												// The earlier list-view used `variant="compact"`
												// + `layout="horizontal"` which rendered a 96×32
												// rectangle — visually disconnected from the
												// grid's circular control. Same `inStock` gate
												// applies; no `layout` prop (floating doesn't
												// take one).
												<div className="w-full flex justify-end">
													<AddToCartControl
														itemId={item.id}
														variant="floating"
														inStock={item.inStock}
														deaSchedule={item.designations.deaSchedule}
													/>
												</div>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					)}

					{/* Empty state */}
					{!catalogLoading && items.length === 0 && (
						<div className="mt-12 text-center py-12">
							<FontAwesomeIcon
								icon={faMagnifyingGlass}
								className="text-4xl text-disabled mb-4"
							/>
							<p className="text-sm font-medium text-strong mb-1">
								{t('No products found')}
							</p>
							<p className="text-xs text-brand-muted mb-4">
								{t('Try adjusting your search or filters.')}
							</p>
							{hasActiveFilters && (
								<button
									type="button"
									onClick={handleClearFilters}
									className="text-sm text-portal-accent hover:underline"
								>
									{t('Clear all filters')}
								</button>
							)}
						</div>
					)}

					{/* Pagination */}
					{pagination != null && (
						<Paginator
							currentPage={currentPage}
							totalPages={pagination.totalPages}
							onPageChange={handlePageChange}
						/>
					)}
				</div>
			</div>

			{/* Mobile category sheet overlay */}
			{mobileFiltersOpen && (
				<div className="fixed inset-0 z-50 md:hidden">
					{/* Backdrop */}
					<div
						className="absolute inset-0 bg-black/50"
						onClick={() => {
							setMobileFiltersOpen(false);
						}}
					/>
					{/* Sheet */}
					<div className="absolute right-0 top-0 bottom-0 w-72 bg-brand-surface shadow-xl overflow-y-auto">
						<div className="flex items-center justify-between p-4 border-b border-brand-border">
							<h2 className="text-sm font-semibold text-default">
								{t('Filters')}
							</h2>
							<button
								type="button"
								onClick={() => {
									setMobileFiltersOpen(false);
								}}
								className="w-8 h-8 flex items-center justify-center text-brand-muted hover:text-strong min-w-touch min-h-touch"
								aria-label={t('Close')}
							>
								<FontAwesomeIcon icon={faTimes} />
							</button>
						</div>
						<div className="p-4">
							{renderCategorySection()}
							{filterFields.length > 0 && (
								<div className="mt-4 pt-4 border-t border-brand-border">
									<h2 className="text-sm font-semibold text-default mb-3">
										{t('Filters')}
									</h2>
									<FilterRail
										fields={filterFields}
										facets={facets}
										searchParams={searchParams}
										onUpdate={handleFacetUpdate}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
